import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormControl, InputGroup } from 'react-bootstrap';
import Nav from 'react-bootstrap/Nav';
import Tab from 'react-bootstrap/Tab';
import { isEmpty } from 'lodash';

/*
Could potentially be integrated with inputGroup,
except InputGroup uses Formik specifically and that
is not needed here.
*/

const SimpleAuditTableSearch = ({
  searchValue,
  onChange,
  onClear,
}) => {
  const handleChange = e => {
    const { value } = e.target;
    onChange(value);
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <div className="form-group d-print-none">
      <br />
      <div className="input-group">
        <input
          type="search"
          id="tableSimpleSearchInput"
          name="tableSimpleSearchInput"
          className="form-control"
          value={searchValue}
          onChange={handleChange}
          placeholder="Search..."
          aria-label="Table Simple Search"
          aria-describedby="tableSearchButton"
          autoComplete="off"
        />
        <div className="input-group-append">
          <button
            type="button"
            id="clearSearchButton"
            name="clearSearchButton"
            className="btn btn-primary"
            aria-label="Clear Search Button"
            onClick={handleClear}
            disabled={searchValue === ''}
          >
            <i className="fas fa-times" />
          </button>
          <button
            type="button"
            id="simpleSearchButton"
            name="simpleSearchButton"
            className="btn btn-primary"
            aria-label="Simple Search Button"
          >
            <i className="fas fa-search" />
          </button>
        </div>
      </div>
    </div>
  );
};

SimpleAuditTableSearch.propTypes = {
  searchValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

const ClearableInput = ({ fieldName, inputObject, onInputChange }) => {
  const handleInputChange = e => {
    const { value } = e.target;
    onInputChange(value);
    // eslint-disable-next-line no-param-reassign
    inputObject[fieldName] = value;
  };

  const handleInputClear = () => {
    // eslint-disable-next-line no-param-reassign
    delete inputObject[fieldName];
    onInputChange('');
    // eslint-disable-next-line no-param-reassign
    inputObject[fieldName] = '';
  };

  const inputGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  };

  const inputStyle = {
    flex: '1',
  };

  const clearButtonStyle = {
    position: 'absolute',
    top: '50%',
    right: '1em',
    paddingRight: '0',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    zIndex: '100',
    background: 'transparent',
    border: 'none',
  };

  return (
    <InputGroup style={inputGroupStyle}>
      <FormControl
        type="text"
        value={inputObject[fieldName]}
        onChange={handleInputChange}
        style={inputStyle}
      />
      {inputObject[fieldName] && (
        <InputGroup.Text onClick={handleInputClear} style={clearButtonStyle}>
          <i className="fas fa-times" />
        </InputGroup.Text>
      )}
    </InputGroup>
  );
};

ClearableInput.propTypes = {
  fieldName: PropTypes.string,
  inputObject: PropTypes.shape().isRequired,
  onInputChange: PropTypes.func.isRequired,
};

const AdvancedAuditTableSearch = ({
  searchObject,
  onChange,
  onClear,
}) => {
  const handleChange = (fieldName, value) => {
    const object = {
      ...searchObject,
      [fieldName]: value,
    };
    onChange(object);
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <div className="form-group d-print-none">
      <br />
      <form>
        <div className="form-row">
          <div className="form-group col-md-2">
            <label htmlFor="inputTransactionType">Transaction Type</label>
            <ClearableInput fieldName="transactionType" inputObject={searchObject} onInputChange={value => handleChange('transactionType', value)} />
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="inputChangedBy">Changed By</label>
            <ClearableInput fieldName="changedBy" inputObject={searchObject} onInputChange={value => handleChange('changedBy', value)} />
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="inputImpersonating">Impersonating</label>
            <ClearableInput fieldName="impersonating" inputObject={searchObject} onInputChange={value => handleChange('impersonating', value)} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group col-md-1">
            <label htmlFor="inputResourceID">Resource ID</label>
            <ClearableInput fieldName="resourceID" inputObject={searchObject} onInputChange={value => handleChange('resourceID', value)} />
          </div>
          <div className="form-group col-md-2">
            <label htmlFor="inputFieldType">Field Type</label>
            <ClearableInput fieldName="fieldType" inputObject={searchObject} onInputChange={value => handleChange('fieldType', value)} />
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="inputFieldName">Field Name</label>
            <ClearableInput fieldName="fieldName" inputObject={searchObject} onInputChange={value => handleChange('fieldName', value)} />
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="inputChangedFrom">Changed From</label>
            <ClearableInput fieldName="changedFrom" inputObject={searchObject} onInputChange={value => handleChange('changedFrom', value)} />
          </div>
          <div className="form-group col-md-3">
            <label htmlFor="inputChangedTo">Changed To</label>
            <ClearableInput fieldName="changedTo" inputObject={searchObject} onInputChange={value => handleChange('changedTo', value)} />
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleClear}
              disabled={Object.values(searchObject).every(value => isEmpty(value))}
            >
              <i className="fas fa-times" />
              &nbsp;
              Clear
            </button>
            <button
              type="button"
              id="simpleSearchButton"
              name="simpleSearchButton"
              className="btn btn-primary"
              aria-label="Simple Search Button"
            >
              <i className="fas fa-search" />
              &nbsp;
              Search
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

AdvancedAuditTableSearch.propTypes = {
  searchObject: PropTypes.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

const AuditTableSearchInputGroup = ({
  setUsingAdvancedSearch,
  onSearch,
}) => {
  const blankAdvancedSearchObject = {
    transactionType: '',
    changedBy: '',
    impersonating: '',
    resourceID: '',
    fieldType: '',
    fieldName: '',
    changedFrom: '',
    changedTo: '',
  };
  const [simpleSearchFieldValue, setSimpleSearchFieldValue] = useState('');
  const [advancedSearchObject, setAdvancedSearchObject] = useState(blankAdvancedSearchObject);
  const [advancedSearchIsActive, setAdvancedSearchIsActive] = useState(false);
  const [key, setKey] = useState('simpleSearch');

  const handleAdvancedSearchChange = value => {
    onSearch(value);
    setAdvancedSearchObject(value);
  };

  const handleSingleSearchChange = value => {
    onSearch({ searchText: value });
    setSimpleSearchFieldValue(value);
  };

  const handleSearchClear = () => {
    if (advancedSearchIsActive) {
      handleAdvancedSearchChange(blankAdvancedSearchObject);
    } else {
      handleSingleSearchChange('');
    }
  };

  const handleSearchTypeChanged = k => {
    setKey(k);
    const usingAdvancedSearch = k === 'advancedSearch';
    setAdvancedSearchIsActive(usingAdvancedSearch);
    setUsingAdvancedSearch(usingAdvancedSearch);
    if (usingAdvancedSearch) {
      handleAdvancedSearchChange(blankAdvancedSearchObject);
    } else {
      handleSingleSearchChange('');
    }
  };

  return (
    <>
      <Tab.Container
        id="auditLogSearchContainer"
        activeKey={key}
        onSelect={handleSearchTypeChanged}
      >
        <div>
          <Nav id="auditLogSearchNav" as="ul" className="simple-search-nav-link nav-tabs-clean">
            <Nav.Item as="li">
              <Nav.Link eventKey="simpleSearch">Search</Nav.Link>
            </Nav.Item>
            <Nav.Item as="li">
              <Nav.Link eventKey="advancedSearch">Advanced Search</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="advancedSearch">
              <AdvancedAuditTableSearch
                key="advanced"
                searchObject={advancedSearchObject}
                onChange={handleAdvancedSearchChange}
                onClear={handleSearchClear}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="simpleSearch">
              <SimpleAuditTableSearch
                key="simple"
                searchValue={simpleSearchFieldValue}
                onChange={handleSingleSearchChange}
                onClear={handleSearchClear}
              />
            </Tab.Pane>
          </Tab.Content>
        </div>
      </Tab.Container>
      <br />
    </>
  );
};

AuditTableSearchInputGroup.propTypes = {
  setUsingAdvancedSearch: PropTypes.func,
  onSearch: PropTypes.func.isRequired,
};

export default AuditTableSearchInputGroup;
